import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { antigenApi, coverageMetricApi, riskCohortApi } from "api/immunization";
import {
  NUMERATOR_RULES,
  SEX_ELIGIBILITY,
  humanise,
} from "config/immunizationConstants";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import AgeBandEditor from "./AgeBandEditor";
import {
  isSet,
  numberOrNull,
  validateCode,
  validateRange,
  validateRequired,
} from "./common/validation";

const toDateInput = (value) =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

const CoverageMetricModal = ({ open, onClose, data }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [form, setForm] = useState({});
  const [antigens, setAntigens] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      name: data?.name || "",
      description: data?.description || "",
      standardBody: data?.standardBody || "",
      effectiveFrom: toDateInput(data?.effectiveFrom),
      effectiveTo: toDateInput(data?.effectiveTo),
      cohortCode: data?.denominator?.cohortCode || "",
      denominatorSex: data?.denominator?.sex || "ANY",
      denominatorMinAgeDays: data?.denominator?.minAgeDays ?? "",
      denominatorMaxAgeDays: data?.denominator?.maxAgeDays ?? "",
      numeratorAntigenCodes: data?.numeratorAntigenCodes || [],
      numeratorRule: data?.numeratorRule || "PER_ANTIGEN",
      countDeclinesSeparately:
        data?.countDeclinesSeparately === undefined ?
          true
        : data.countDeclinesSeparately,
      ageBands: (data?.ageBands || []).map((band) => ({
        ...band,
        maxAgeDays: band.maxAgeDays ?? "",
      })),
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  useEffect(() => {
    if (!open) return;
    antigenApi
      .active()
      .then((res) => setAntigens(res?.data?.data || []))
      .catch(() => toast.error("Could not load antigens"));
    riskCohortApi
      .active()
      .then((res) => setCohorts(res?.data?.data || []))
      .catch(() => toast.error("Could not load risk cohorts"));
  }, [open]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const bands = form.ageBands || [];

  const validationError = useMemo(() => {
    const base =
      validateCode(form.code, { isUpdate, label: "Metric code" }) ||
      validateRequired(form.name, "Metric name");
    if (base) return base;

    if (!isUpdate && !form.effectiveFrom) return "Effective from is required";
    if (
      form.effectiveFrom &&
      form.effectiveTo &&
      new Date(form.effectiveTo) <= new Date(form.effectiveFrom)
    ) {
      return "Effective to must be later than effective from";
    }

    if (!(form.numeratorAntigenCodes || []).length) {
      return "Select at least one numerator antigen";
    }

    const denomRange = validateRange(
      form.denominatorMinAgeDays,
      form.denominatorMaxAgeDays,
      "Denominator age",
    );
    if (denomRange) return denomRange;

    const seen = new Set();
    for (let i = 0; i < bands.length; i += 1) {
      const band = bands[i];
      const position = band.code || `Band ${i + 1}`;
      if (!String(band.code || "").trim())
        return `Band ${i + 1}: code is required`;
      if (!String(band.label || "").trim())
        return `${position}: label is required`;
      if (!isSet(band.minAgeDays))
        return `${position}: minimum age is required`;

      const key = String(band.code).trim().toUpperCase();
      if (seen.has(key)) return `Duplicate age band code "${band.code}"`;
      seen.add(key);

      const range = validateRange(band.minAgeDays, band.maxAgeDays, position);
      if (range) return range;
    }

    return "";
  }, [form, bands, isUpdate]);

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || "",
      standardBody: form.standardBody?.trim() || "",
      ...(isUpdate ? {} : { effectiveFrom: form.effectiveFrom }),
      effectiveTo: form.effectiveTo || null,
      denominator: {
        cohortCode: form.cohortCode || null,
        sex: form.denominatorSex,
        minAgeDays: numberOrNull(form.denominatorMinAgeDays),
        maxAgeDays: numberOrNull(form.denominatorMaxAgeDays),
      },
      numeratorAntigenCodes: form.numeratorAntigenCodes,
      numeratorRule: form.numeratorRule,
      countDeclinesSeparately: form.countDeclinesSeparately,
      ageBands: bands.map((band) => ({
        code: band.code.trim(),
        label: band.label.trim(),
        minAgeDays: Number(band.minAgeDays),
        maxAgeDays: numberOrNull(band.maxAgeDays),
        sequence: Number(band.sequence) || 0,
      })),
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await coverageMetricApi.update(recordId, payload);
        toast.success("Coverage metric updated successfully");
      } else {
        await coverageMetricApi.create({ ...payload, code: form.code.trim() });
        toast.success("Coverage metric version created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} coverage metric`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Coverage Metric"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
      maxWidth="md"
    >
      <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
        Coverage metrics are versioned by effective date. To revise a
        definition, close the current version by setting its "effective to" date
        and add a new version with the same code — editing a live definition in
        place would stop past reports reproducing the numbers already submitted.
      </Alert>

      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Metric Code"
          helperText='Shared across versions, e.g. "nabh mod1 diabetes"'
        />
        <TextField
          label="Metric Name"
          required
          fullWidth
          size="small"
          value={form.name || ""}
          onChange={(event) => set({ name: event.target.value })}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Standard Body"
          fullWidth
          size="small"
          value={form.standardBody || ""}
          onChange={(event) => set({ standardBody: event.target.value })}
          helperText="e.g. NABH, WHO, IAP, INTERNAL"
        />
        <TextField
          label="Effective From"
          type="date"
          required
          size="small"
          sx={{ minWidth: 180 }}
          InputLabelProps={{ shrink: true }}
          value={form.effectiveFrom || ""}
          disabled={isUpdate}
          onChange={(event) => set({ effectiveFrom: event.target.value })}
          helperText={isUpdate ? "Fixed for this version" : ""}
        />
        <TextField
          label="Effective To"
          type="date"
          size="small"
          sx={{ minWidth: 180 }}
          InputLabelProps={{ shrink: true }}
          value={form.effectiveTo || ""}
          onChange={(event) => set({ effectiveTo: event.target.value })}
          helperText="Blank = still in force"
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Divider />

      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Denominator — the population measured
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          select
          label="Risk Cohort"
          size="small"
          sx={{ minWidth: 220 }}
          value={form.cohortCode || ""}
          onChange={(event) => set({ cohortCode: event.target.value })}
          helperText="Blank = all patients"
        >
          <MenuItem value="">All patients</MenuItem>
          {cohorts.map((cohort) => (
            <MenuItem key={cohort.code} value={cohort.code}>
              {cohort.cohortName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sex"
          size="small"
          sx={{ minWidth: 130 }}
          value={form.denominatorSex || "ANY"}
          onChange={(event) => set({ denominatorSex: event.target.value })}
        >
          {SEX_ELIGIBILITY.map((option) => (
            <MenuItem key={option} value={option}>
              {humanise(option)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Min Age (days)"
          type="number"
          size="small"
          sx={{ minWidth: 150 }}
          value={form.denominatorMinAgeDays}
          onChange={(event) =>
            set({ denominatorMinAgeDays: event.target.value })
          }
        />
        <TextField
          label="Max Age (days)"
          type="number"
          size="small"
          sx={{ minWidth: 150 }}
          value={form.denominatorMaxAgeDays}
          onChange={(event) =>
            set({ denominatorMaxAgeDays: event.target.value })
          }
        />
      </Box>

      <Divider />

      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Numerator — the protection counted
      </Typography>

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={antigens}
        value={antigens.filter((antigen) =>
          (form.numeratorAntigenCodes || []).includes(antigen.code),
        )}
        getOptionLabel={(option) => option?.antigenName || ""}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        onChange={(event, next) =>
          set({ numeratorAntigenCodes: next.map((item) => item.code) })
        }
        renderInput={(params) => (
          <TextField {...params} label="Numerator Antigens" required />
        )}
      />

      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          select
          label="Numerator Rule"
          size="small"
          sx={{ minWidth: 210 }}
          value={form.numeratorRule || "PER_ANTIGEN"}
          onChange={(event) => set({ numeratorRule: event.target.value })}
        >
          {NUMERATOR_RULES.map((rule) => (
            <MenuItem key={rule} value={rule}>
              {humanise(rule)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.countDeclinesSeparately)}
              onChange={(event) =>
                set({ countDeclinesSeparately: event.target.checked })
              }
            />
          }
          label="Report documented declines separately"
        />
      </Box>

      <Divider />

      <AgeBandEditor
        value={bands}
        onChange={(next) => set({ ageBands: next })}
        disabled={saving}
      />
    </MasterModalShell>
  );
};

export default CoverageMetricModal;
